import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useRealtime } from '../providers/RealtimeProvider';
import API from '../services/api';
import logo from '../assets/logo.png';
import { clearAdminSession } from '../utils/authStorage';

const Icon = ({ children }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const icons = {
  dashboard: (
    <Icon>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Icon>
  ),
  intelligence: (
    <Icon>
      <path d="M12 3v3" />
      <path d="M12 18v3" />
      <path d="M4.2 7.5 6.8 9" />
      <path d="m17.2 15 2.6 1.5" />
      <path d="m4.2 16.5 2.6-1.5" />
      <path d="m17.2 9 2.6-1.5" />
      <circle cx="12" cy="12" r="5" />
      <path d="M10 12h4" />
      <path d="M12 10v4" />
    </Icon>
  ),
  admins: (
    <Icon>
      <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Z" />
      <path d="M3 22a9 9 0 0 1 18 0" />
    </Icon>
  ),
  actives: (
    <Icon>
      <path d="M4 18c2-2.5 4-4 8-4s6 1.5 8 4" />
      <circle cx="12" cy="7" r="3.5" />
    </Icon>
  ),
  bookings: (
    <Icon>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <path d="M6 10h12" />
    </Icon>
  ),
  fares: (
    <Icon>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 8v8" />
      <path d="M15 9h-3a2 2 0 0 0 0 4h0a2 2 0 0 1 0 4h-3" />
    </Icon>
  ),
  reports: (
    <Icon>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 8h10" />
      <path d="M7 12h6" />
      <path d="M7 16h8" />
    </Icon>
  ),
  messaging: (
    <Icon>
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    </Icon>
  ),
  create: (
    <Icon>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </Icon>
  ),
  logout: (
    <Icon>
      <path d="M10 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2v-2" />
      <path d="m15 12-8 0" />
      <path d="m7 9-3 3 3 3" />
    </Icon>
  ),
  enrollme: (
    <Icon>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6" />
      <path d="M9 16h4" />
    </Icon>
  ),
  accidents: (
    <Icon>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Icon>
  ),
  dispatchMap: (
    <Icon>
      <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
      <line x1="9" y1="3" x2="9" y2="18" />
      <line x1="15" y1="6" x2="15" y2="21" />
    </Icon>
  ),
  menu: (
    <Icon>
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </Icon>
  ),
  chevronLeft: (
    <Icon>
      <polyline points="15 18 9 12 15 6" />
    </Icon>
  ),
  chevronRight: (
    <Icon>
      <polyline points="9 18 15 12 9 6" />
    </Icon>
  ),
  chevronDown: (
    <Icon>
      <polyline points="6 9 12 15 18 9" />
    </Icon>
  ),
};

const navSections = [
  {
    id: 'dashboard',
    type: 'link',
    to: '/',
    label: 'Dashboard',
    icon: icons.dashboard,
    end: true,
  },
  {
    id: 'operations',
    type: 'group',
    label: 'Operations',
    icon: icons.dispatchMap,
    items: [
      { to: '/operations/dispatch', label: 'Dispatch Map', icon: icons.dispatchMap, end: true },
      { to: '/admins', label: 'Admin Approvals', icon: icons.admins },
      { to: '/actives', label: 'Active Roster', icon: icons.actives },
      { to: '/enrollme-admins', label: 'EnrollMe Admins', icon: icons.enrollme },
    ],
  },
  {
    id: 'intelligence',
    type: 'group',
    label: 'Intelligence',
    icon: icons.intelligence,
    items: [
      { to: '/operations/intelligence', label: 'Intelligence', icon: icons.intelligence, end: true },
      { to: '/operations/intelligence/hos-notifications', label: 'HOS Notifications', icon: icons.messaging, end: true },
      { to: '/reports/pulse', label: 'Pulse History', icon: icons.intelligence, end: true },
      { to: '/reports/motivation-messages', label: 'Motivation Msgs', icon: icons.messaging, end: true },
      { to: '/settings/messaging', label: 'Driver Messaging', icon: icons.messaging, end: true },
    ],
  },
  {
    id: 'bookings',
    type: 'group',
    label: 'Bookings',
    icon: icons.bookings,
    items: [
      { to: '/bookings', label: 'Bookings', icon: icons.bookings },
      { to: '/bookings/new', label: 'Add Booking', icon: icons.create, end: true },
    ],
  },
  {
    id: 'reports',
    type: 'group',
    label: 'Reports',
    icon: icons.reports,
    items: [
      { to: '/reports/driver-safety', label: 'Driver Safety', icon: icons.accidents, end: true },
      { to: '/reports/financials', label: 'Financials', icon: icons.reports, end: true },
      { to: '/reports/builder', label: 'Report Designer', icon: icons.reports, end: true },
      { to: '/reports/receipts', label: 'Receipts', icon: icons.reports, end: true },
      { to: '/reports/diagnostics', label: 'Diagnostics', icon: icons.reports, end: true },
      { to: '/reports/insurance-removal', label: 'Insurance Removal', icon: icons.reports, end: true },
      { to: '/accidents', label: 'Accident Reports', icon: icons.accidents, end: true },
    ],
  },
  {
    id: 'settings',
    type: 'group',
    label: 'Settings',
    icon: icons.fares,
    items: [
      { to: '/fares', label: 'Fares', icon: icons.fares },
      { to: '/settings/company', label: 'Company', icon: icons.fares, end: true },
    ],
  },
];

const isPathMatch = (item, pathname) => {
  if (!item?.to) return false;
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const AppLayout = ({ title, subtitle, actions, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true',
  );

  // Only the active section is open by default — everything else collapsed
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    navSections.forEach((s) => {
      if (s.type === 'group') {
        initial[s.id] = s.items.some((item) => isPathMatch(item, location.pathname));
      }
    });
    return initial;
  });

  const { socket, connected } = useRealtime();
  const [notifications, setNotifications] = useState([]);
  const connectionStateRef = useRef(false);
  const forcedLogoutRef = useRef(false);

  // Auto-expand group when navigating to one of its routes
  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      navSections.forEach((s) => {
        if (s.type !== 'group') return;
        if (s.items.some((item) => isPathMatch(item, location.pathname)) && !next[s.id]) {
          next[s.id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  const toggleGroup = useCallback((id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // In collapsed (icon-rail) mode, clicking a group icon: expand sidebar + open group
  const handleCollapsedGroupClick = useCallback((id) => {
    setSidebarCollapsed(false);
    localStorage.setItem('sidebarCollapsed', 'false');
    setOpenGroups((prev) => ({ ...prev, [id]: true }));
  }, []);

  const toggleCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const pushNotification = useCallback(
    (message, tone = 'info', timeoutMs = 8000) => {
      const id = Date.now() + Math.random();
      setNotifications((prev) => {
        const next = [...prev, { id, message, tone }];
        if (next.length > 4) next.shift();
        return next;
      });
      window.setTimeout(() => dismissNotification(id), timeoutMs);
    },
    [dismissNotification],
  );

  const logout = useCallback(async () => {
    try { await API.post('/admins/logout'); } catch (_) {}
    clearAdminSession();
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const handler = (ev) => {
      try {
        const { message, tone } = ev?.detail || {};
        if (message) pushNotification(message, tone || 'info');
      } catch (_) {}
    };
    window.addEventListener('taxiops:pushNotification', handler);
    return () => window.removeEventListener('taxiops:pushNotification', handler);
  }, [pushNotification]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleAssignmentUpdate = (payload = {}) => {
      const { event, booking } = payload;
      const label = booking?.bookingId ? `Trip #${booking.bookingId}` : 'Trip';
      switch (event) {
        case 'assigned':
          pushNotification(`${label} assigned to ${booking?.driverId || 'a driver'}.`, 'info');
          break;
        case 'declined':
          pushNotification(`${booking?.driverId || 'Driver'} declined ${label}.`, 'warning');
          break;
        case 'status':
          pushNotification(
            `${booking?.driverId || 'Driver'} marked ${label} as ${booking?.status || 'updated'}.`,
            booking?.status === 'Completed' ? 'success' : booking?.status === 'Cancelled' ? 'warning' : 'info',
          );
          break;
        case 'flagdown':
          pushNotification(`${booking?.driverId || 'Driver'} captured a flagdown ride.`, 'info');
          break;
        default:
          pushNotification(`Dispatch update for ${label}.`, 'info');
      }
    };

    const handleMessageScheduled = (payload = {}) => {
      pushNotification(`Scheduled "${payload?.message?.title || 'Driver message'}".`, 'info');
    };

    const handleMessageCancelled = (payload = {}) => {
      pushNotification(`Cancelled "${payload?.message?.title || 'Driver message'}".`, 'warning');
    };

    const handleSessionInvalidated = (payload = {}) => {
      if (forcedLogoutRef.current) return;
      forcedLogoutRef.current = true;
      pushNotification(
        payload?.message || 'This admin account was signed in somewhere else. This session has ended.',
        'warning',
      );
      logout();
    };

    const handleSos = (payload = {}) => {
      const cab = payload.cabNumber ? ` · Cab ${payload.cabNumber}` : '';
      pushNotification(`SOS ALERT: ${payload.driverName || payload.driverId || 'Driver'}${cab}. Open Driver Safety now.`, 'critical', 30000);
    };

    const handleSosDelivery = (payload = {}) => {
      if (payload.emailStatus !== 'sent') pushNotification(`SOS email ${payload.emailStatus || 'is pending'}. Contact the driver and emergency services directly if required.`, 'warning', 30000);
    };

    socket.on('assignment:updated', handleAssignmentUpdate);
    socket.on('message:scheduled', handleMessageScheduled);
    socket.on('message:cancelled', handleMessageCancelled);
    socket.on('auth:kicked', handleSessionInvalidated);
    socket.on('session:invalidated', handleSessionInvalidated);
    socket.on('safety:sos', handleSos);
    socket.on('safety:sos_delivery', handleSosDelivery);

    return () => {
      socket.off('assignment:updated', handleAssignmentUpdate);
      socket.off('message:scheduled', handleMessageScheduled);
      socket.off('message:cancelled', handleMessageCancelled);
      socket.off('auth:kicked', handleSessionInvalidated);
      socket.off('session:invalidated', handleSessionInvalidated);
      socket.off('safety:sos', handleSos);
      socket.off('safety:sos_delivery', handleSosDelivery);
    };
  }, [socket, pushNotification, logout]);

  useEffect(() => {
    if (!socket) return;
    if (connected && !connectionStateRef.current) {
      connectionStateRef.current = true;
      pushNotification('Realtime updates connected.', 'success');
    } else if (!connected && connectionStateRef.current) {
      connectionStateRef.current = false;
      pushNotification('Realtime connection lost. Attempting to reconnect…', 'warning');
    }
  }, [connected, socket, pushNotification]);

  const toggleSidebar = () => setSidebarOpen((p) => !p);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className={`app-shell ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {notifications.length > 0 && (
        <div className="realtime-toast-container">
          {notifications.map((note) => (
            <div key={note.id} className={`realtime-toast realtime-toast--${note.tone}`}>
              <span>{note.message}</span>
              <button type="button" aria-label="Dismiss" onClick={() => dismissNotification(note.id)}>
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <aside className={`app-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <img src={logo} alt="TaxiOps" className="sidebar-logo" />
          <div className="brand-text">
            <span className="app-name">TaxiOps</span>
            <span className="app-tagline">Control Center</span>
          </div>
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? icons.chevronRight : icons.chevronLeft}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navSections.map((section) => {
            // ── Top-level direct link (Dashboard)
            if (section.type === 'link') {
              return (
                <NavLink
                  key={section.id}
                  to={section.to}
                  end={section.end}
                  className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
                  onClick={closeSidebar}
                  title={sidebarCollapsed ? section.label : undefined}
                  data-label={section.label}
                >
                  <span className="sidebar-link-icon">{section.icon}</span>
                  <span className="sidebar-label">{section.label}</span>
                </NavLink>
              );
            }

            // ── Group
            const expanded = !!openGroups[section.id];
            const sectionActive = section.items.some((item) => isPathMatch(item, location.pathname));

            // In collapsed (icon-rail) mode, show only the group icon button
            if (sidebarCollapsed) {
              return (
                <button
                  key={section.id}
                  type="button"
                  className={`sidebar-rail-group${sectionActive ? ' active' : ''}`}
                  onClick={() => handleCollapsedGroupClick(section.id)}
                  title={section.label}
                  data-label={section.label}
                >
                  <span className="sidebar-link-icon">{section.icon}</span>
                </button>
              );
            }

            // Expanded mode: full accordion group
            return (
              <div key={section.id} className={`sidebar-group${expanded ? ' open' : ''}${sectionActive ? ' group-active' : ''}`}>
                <button
                  type="button"
                  className={`sidebar-group-toggle${sectionActive ? ' active' : ''}`}
                  onClick={() => toggleGroup(section.id)}
                  aria-expanded={expanded}
                >
                  <span className="sidebar-link-icon">{section.icon}</span>
                  <span className="sidebar-label">{section.label}</span>
                  <span className="sidebar-group-chevron" aria-hidden="true">{icons.chevronDown}</span>
                </button>

                <div className="sidebar-subnav">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => `sidebar-link sidebar-sub-link${isActive ? ' active' : ''}`}
                      onClick={closeSidebar}
                    >
                      <span className="sidebar-link-icon">{item.icon}</span>
                      <span className="sidebar-label">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-status" title={connected ? 'Realtime connected' : 'Reconnecting…'}>
            <span className={`status-dot ${connected ? 'status-dot--live' : 'status-dot--offline'}`} />
            <span className="sidebar-footer-text">{connected ? 'Live' : 'Reconnecting…'}</span>
          </div>
          <button type="button" className="sidebar-logout" onClick={logout} title="Sign out">
            <span className="sidebar-link-icon">{icons.logout}</span>
            <span className="sidebar-label">Sign out</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeSidebar} role="presentation" />}

      <div className="app-content">
        <header className="app-header">
          <button type="button" className="header-menu" onClick={toggleSidebar} aria-label="Toggle navigation">
            {icons.menu}
          </button>
          <div className="header-titles">
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="header-actions">{actions}</div>}
        </header>
        <main className="app-main">{children}</main>
        <footer className="app-footer">
          <div className="app-footer-inner">
            <span>© {new Date().getFullYear()} Developed and owned by Old Alex Hub, a Florida, USA LLC</span>
            <div className="app-footer-store-links" aria-label="TaxiOps Driver app download links">
              <a href="https://play.google.com/store/apps/details?id=com.oldalexhub.taxiops.driver" target="_blank" rel="noreferrer" aria-label="Get TaxiOps Driver on Google Play" className="app-footer-store-link">
                <svg aria-hidden="true" width="104" height="32" viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                  <rect width="135" height="40" rx="6" fill="#000"/>
                  <path d="M19.5 13.5 28 20l-8.5 6.5V13.5z" fill="#fff" opacity=".9"/>
                  <text x="33" y="17" fontFamily="Arial, sans-serif" fontSize="8" fill="#fff" opacity=".7">GET IT ON</text>
                  <text x="33" y="28" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="bold" fill="#fff">Google Play</text>
                </svg>
              </a>
              <a href="https://apps.apple.com/us/app/taxiops-driver/id6775650748" target="_blank" rel="noreferrer" aria-label="Download TaxiOps Driver on the App Store" className="app-footer-store-link">
                <svg aria-hidden="true" width="104" height="32" viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                  <rect width="135" height="40" rx="6" fill="#000"/>
                  <path d="M22.7 12.1c.9-1 1.5-2.5 1.3-3.8-1.3.1-2.8.8-3.7 1.9-.9 1-1.5 2.4-1.4 3.7 1.4.1 2.9-.8 3.8-1.8z" fill="#fff"/>
                  <path d="M27.6 23.1c-.7 1.6-1.1 2.3-2.2 3.7-1.4 1.9-3.2 4.1-5.5 4.1-2 0-2.6-1.3-5.4-1.3s-3.4 1.3-5.4 1.4c-2.3.1-4-2-5.4-3.9-3.7-5.3-4.1-11.5-1.7-14.8 1.6-2.3 4.2-3.7 6.6-3.7 2.5 0 4 1.4 6 1.4 2 0 3.2-1.4 6-1.4 2.1 0 4.4 1.2 6 3.2-5.3 2.9-4.4 10.4 1 11.3z" fill="#fff" transform="translate(5 2) scale(.72)"/>
                  <text x="43" y="17" fontFamily="Arial, sans-serif" fontSize="8" fill="#fff" opacity=".7">Download on the</text>
                  <text x="43" y="28" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="bold" fill="#fff">App Store</text>
                </svg>
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AppLayout;
